/**
 * useArbitrageConfigs Hook
 *
 * Fetches arbitrage configurations for a specific project with React Query caching.
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import type { IArbitrageConfig } from 'types/arbitrage';

import { ARBITRAGE_QUERY_KEYS, getArbitrageConfigs } from '../api';

type QueryOptions<TData = IArbitrageConfig[]> = Omit<
  UseQueryOptions<IArbitrageConfig[], Error, TData>,
  'queryFn' | 'queryKey'
>;

export interface UseArbitrageConfigsParams {
  projectId: number;
}

export const useArbitrageConfigs = <TData = IArbitrageConfig[]>(
  params: UseArbitrageConfigsParams,
  queryOpts?: QueryOptions<TData>,
) => {
  const { projectId } = params;
  const defaultQueryOpts: QueryOptions<TData> = {
    staleTime: 5 * 60 * 1000, // 5 minutes (arbitrages change rarely)
  };

  return useQuery<IArbitrageConfig[], Error, TData>({
    queryKey: ARBITRAGE_QUERY_KEYS.listByProject(projectId),
    queryFn: async ({ signal }) => {
      const response = await getArbitrageConfigs({ projectId, signal });

      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || 'Failed to fetch arbitrage configs');
      }

      return response.data;
    },
    ...defaultQueryOpts,
    ...queryOpts,
  });
};
